import { motion } from 'motion/react';
import { useMemo } from 'react';

interface ToolCallDetailsProps {
  toolName: string;
  state: string;
  progressStage?: string;
}

const FALLBACK_LABEL = 'Working';

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  queryData: 'Looking things up',
  getDocSection: 'Checking the docs',
};

const PROGRESS_LABELS: Record<string, string> = {
  'finding-data': 'Looking things up',
  'writing-sql': 'Planning the query',
  'querying-database': 'Pulling the data',
};

const SHIMMER_REFERENCE_LENGTH = 20;

export function TextShimmer({
  children,
  duration = 2,
  spread = 2,
}: {
  children: string;
  duration?: number;
  spread?: number;
}) {
  const dynamicSpread = useMemo(() => children.length * spread, [children, spread]);
  const normalizedDuration = useMemo(
    () => duration * (children.length / SHIMMER_REFERENCE_LENGTH),
    [duration, children.length]
  );

  return (
    <motion.p
      className="relative inline-block bg-clip-text text-transparent text-sm"
      initial={{ backgroundPosition: '100% center' }}
      animate={{ backgroundPosition: '0% center' }}
      transition={{ repeat: Infinity, duration: normalizedDuration, ease: 'linear' }}
      style={
        {
          backgroundImage: `linear-gradient(90deg, #0000 calc(50% - ${dynamicSpread}px), #000, #0000 calc(50% + ${dynamicSpread}px)), linear-gradient(#a1a1aa, #a1a1aa)`,
          backgroundSize: '250% 100%, auto',
          backgroundRepeat: 'no-repeat, padding-box',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any
      }
    >
      {children}
    </motion.p>
  );
}

export function ToolCallDetails({ toolName, state, progressStage }: ToolCallDetailsProps) {
  if (state === 'output-available') return null;

  const label = progressStage
    ? (PROGRESS_LABELS[progressStage] ?? TOOL_DISPLAY_NAMES[toolName] ?? FALLBACK_LABEL)
    : (TOOL_DISPLAY_NAMES[toolName] ?? FALLBACK_LABEL);

  return <TextShimmer duration={1.5}>{`${label} ...`}</TextShimmer>;
}
